import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { analyticsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, TrendingUp, Eye, MousePointer, Share2, Users, Calendar
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const CHART_COLORS = [
  'hsl(174, 74%, 34%)', 'hsl(199, 84%, 48%)', 'hsl(40, 92%, 56%)',
  'hsl(142, 72%, 44%)', 'hsl(0, 72%, 52%)', '#E1306C', '#0A66C2'
];

export default function AnalyticsPage() {
  const { currentWorkspace } = useWorkspace();
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      Promise.all([
        analyticsAPI.get(currentWorkspace.id, parseInt(days)),
        analyticsAPI.chartData(currentWorkspace.id, parseInt(days)),
      ])
        .then(([analyticsRes, chartRes]) => {
          setAnalytics(analyticsRes.data);
          setChartData(chartRes.data);
        })
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id, days]);

  const overview = analytics?.overview || {};

  const kpis = [
    { icon: BarChart3, label: 'Total Posts', value: overview.total_posts || 0, color: 'text-primary' },
    { icon: TrendingUp, label: 'Published', value: overview.published_posts || 0, color: 'text-emerald-400' },
    { icon: Calendar, label: 'Scheduled', value: overview.scheduled_posts || 0, color: 'text-cyan-400' },
    { icon: Eye, label: 'Media Assets', value: overview.total_media || 0, color: 'text-blue-400' },
    { icon: MousePointer, label: 'Campaigns', value: overview.total_campaigns || 0, color: 'text-orange-400' },
    { icon: Users, label: 'Connected', value: overview.connected_platforms || 0, color: 'text-purple-400' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover/90 backdrop-blur border rounded-lg p-3 text-xs shadow-lg">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color }}>
              {p.name}: {p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-secondary rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-secondary rounded-xl" />)}
        </div>
        <div className="h-64 bg-secondary rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Track performance across all platforms.</p>
        </div>
        <Select value={days} onValueChange={setDays} data-testid="analytics-date-range-select">
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="rounded-xl border bg-card/60 backdrop-blur shadow-sm">
            <CardContent className="p-4">
              <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center mb-2">
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
              <p className="text-xl font-display font-semibold" data-font="display">{kpi.value}</p>
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Trend */}
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Activity Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData?.daily?.slice(-14) || []}>
                <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.35} />
                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="posts" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.15} strokeWidth={2} name="Posts" />
                <Area type="monotone" dataKey="engagement" stroke={CHART_COLORS[1]} fill={CHART_COLORS[1]} fillOpacity={0.1} strokeWidth={2} name="Engagement" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Platform Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {(chartData?.platforms || []).length === 0 ? (
              <div className="flex items-center justify-center h-[280px]">
                <p className="text-sm text-muted-foreground">No platform data yet. Create posts to see distribution.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chartData?.platforms || []}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                  >
                    {(chartData?.platforms || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Reach Trend */}
        <Card className="bg-card/60 backdrop-blur lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Estimated Reach</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData?.daily?.slice(-14) || []}>
                <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.35} />
                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="reach" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} name="Reach" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Meta Pixel Card */}
      <Card className="bg-card/60 backdrop-blur">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
              <Share2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium text-sm">Meta Pixel Integration</p>
              <p className="text-xs text-muted-foreground mt-1">
                Configure your Meta Pixel ID in Settings to track conversions, retarget audiences, and pull deep analytics data into this dashboard.
              </p>
              <Badge className="mt-2 text-xs bg-secondary text-muted-foreground">
                {currentWorkspace?.meta_pixel_id ? 'Configured' : 'Not Configured'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
